import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type StandingRow, type ProgressionClan } from '../lib/api';
import { cn } from '../lib/cn';
import { Card } from '../components/Card';
import { StatusBadge } from '../components/StatusBadge';
import { PageSpinner } from '../components/Spinner';
import { EmptyState } from '../components/EmptyState';
import { useWorkspace } from '../lib/workspace';
import { isAdminRole } from '../lib/roles';
import { useEffectiveRole } from '../lib/viewAs';
import { PublicPageToggle } from '../components/PublicPageToggle';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import { Trophy, Swords, ChevronRight, Users, ChevronDown, Download, TrendingUp } from 'lucide-react';

function formatMatchDate(ts?: number) {
  if (!ts) return null;
  const d = new Date(ts);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) +
    ', ' +
    d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function ExportStatsButton({ compId, compName }: { compId: string; compName: string }) {
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const csv = await api.exportCompStatsCsv(compId);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${compName.replace(/[^\w-]+/g, '-').toLowerCase()}-final-stats.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={download}
      disabled={busy}
      className={cn('flex shrink-0 items-center gap-2 rounded-lg bg-white/[0.05] px-3 py-2 text-xs font-semibold uppercase tracking-wider text-ink-secondary transition-colors hover:bg-white/[0.08] hover:text-ink-primary disabled:opacity-50', MORPH)}
      title="Download season stats (Final Stats template columns)"
    >
      <Download className="h-3.5 w-3.5" /> {busy ? 'Exporting…' : 'Export CSV'}
    </button>
  );
}

// ── Season progression — cumulative score per round, top clans ────────────────

const LINE_COLOURS = ['#a78bfa', '#34d399', '#f59e0b', '#f87171', '#38bdf8', '#f472b6', '#a3e635', '#fb923c'];

function ProgressionChart({ clans, rounds }: { clans: ProgressionClan[]; rounds: number }) {
  const top = clans.slice(0, 8);
  const W = 760, H = 240, PL = 56, PR = 8, PT = 10, PB = 26;
  const plotW = W - PL - PR, plotH = H - PT - PB;
  const maxScore = Math.max(1, ...top.flatMap(c => c.perRound.map(p => p.cumScore)));
  const x = (round: number) => PL + (rounds > 1 ? ((round - 1) / (rounds - 1)) * plotW : plotW / 2);
  const y = (score: number) => PT + plotH - (score / maxScore) * plotH;

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Cumulative score per round">
        {[0, 0.25, 0.5, 0.75, 1].map(f => (
          <g key={f}>
            <line x1={PL} x2={W - PR} y1={PT + plotH * f} y2={PT + plotH * f} stroke="#374151" strokeWidth={0.5} strokeDasharray="3 3" />
            <text x={PL - 6} y={PT + plotH * f + 3} textAnchor="end" fontSize={9} fill="#6b7280">
              {Math.round(maxScore * (1 - f)).toLocaleString()}
            </text>
          </g>
        ))}
        {Array.from({ length: rounds }, (_, i) => i + 1).map(r => (
          <text key={r} x={x(r)} y={H - 8} textAnchor="middle" fontSize={9} fill="#6b7280">R{r}</text>
        ))}
        {top.map((c, i) => (
          <polyline
            key={c.clan}
            fill="none"
            stroke={LINE_COLOURS[i % LINE_COLOURS.length]}
            strokeWidth={1.8}
            points={c.perRound.map(p => `${x(p.round)},${y(p.cumScore)}`).join(' ')}
          />
        ))}
      </svg>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
        {top.map((c, i) => (
          <span key={c.clan} className="flex items-center gap-1.5 text-xs text-ink-secondary">
            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: LINE_COLOURS[i % LINE_COLOURS.length] }} />
            {c.display}
          </span>
        ))}
      </div>
    </div>
  );
}

function SeasonProgression({ compId }: { compId: string }) {
  const [division, setDivision] = useState<string>('ALL');
  const { data, isLoading } = useQuery({
    queryKey: ['compProgression', compId],
    queryFn: () => api.compProgression(compId),
    enabled: !!compId,
    staleTime: 60 * 1000,
  });

  if (isLoading || !data || data.rounds < 2 || data.clans.length === 0) return null;

  const divisions = Array.from(new Set(data.clans.map(c => c.division).filter(Boolean)));
  const clans = division === 'ALL' ? data.clans : data.clans.filter(c => c.division === division);

  return (
    <Card className="mt-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-ink-secondary">
          <TrendingUp className="h-4 w-4 text-ink-meta" /> Season Progression
          <span className="rounded bg-white/[0.05] px-1.5 py-0.5 text-xs font-normal text-ink-secondary">{data.rounds} rounds</span>
        </h3>
        {divisions.length > 1 && (
          <select
            value={division}
            onChange={e => setDivision(e.target.value)}
            className="input text-xs"
          >
            <option value="ALL">All divisions</option>
            {divisions.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
      </div>
      {clans.length === 0 ? (
        <p className="text-sm text-gray-500">No played rounds in this division yet.</p>
      ) : (
        <ProgressionChart clans={clans} rounds={data.rounds} />
      )}
    </Card>
  );
}

function StandingsTable({ compId }: { compId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['standings', compId],
    queryFn: () => api.standings(compId),
    enabled: !!compId,
  });

  if (isLoading) return <PageSpinner />;
  if (error) return <p className="text-red-400">Failed to load standings.</p>;
  if (!data) return null;

  const rows = data.standings;
  const isScore = data.standingsType === 'SCORE';

  return (
    <div>
      <p className="mb-4 text-sm text-ink-meta">
        {data.matchCount} confirmed match{data.matchCount !== 1 ? 'es' : ''}
      </p>
      {rows.length === 0 ? (
        <p className="text-ink-meta">No confirmed matches yet — check back after match night.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="rounded-lg bg-white/[0.04] text-left text-xs uppercase tracking-wider text-ink-meta">
                <th className="rounded-l-lg py-2.5 pl-3 pr-4">#</th>
                <th className="py-2.5 pr-4">Clan</th>
                <th className="py-2.5 pr-4 text-center">MP</th>
                {isScore ? (
                  <>
                    <th className="py-2.5 pr-4 text-center">Total Score</th>
                    <th className="py-2.5 pr-4 text-center">Avg Score</th>
                  </>
                ) : (
                  <>
                    <th className="py-2.5 pr-4 text-center">W</th>
                    <th className="py-2.5 pr-4 text-center">D</th>
                    <th className="py-2.5 pr-4 text-center">L</th>
                    <th className="py-2.5 pr-4 text-center">GW</th>
                    <th className="py-2.5 pr-4 text-center">GL</th>
                  </>
                )}
                <th className="rounded-r-lg py-2.5 pr-3 text-center font-bold text-accent-500">PTS</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row: StandingRow, i: number) => (
                <tr
                  key={row.name}
                  className="transition-colors odd:bg-white/[0.02] hover:bg-white/[0.05]"
                >
                  <td className={cn('py-3 pl-3 pr-4 font-mono', i === 0 ? 'text-accent-500' : 'text-ink-meta')}>{i + 1}</td>
                  <td className="py-3 pr-4">
                    <Link
                      to={`/clan/${encodeURIComponent(row.name)}`}
                      className="font-medium text-ink-primary hover:text-accent-500"
                    >
                      {row.name}
                    </Link>
                  </td>
                  <td className="py-3 pr-4 text-center font-mono text-ink-secondary">{row.matchesPlayed}</td>
                  {isScore ? (
                    <>
                      <td className="py-3 pr-4 text-center font-mono text-ink-secondary">{(row.totalScore ?? 0).toLocaleString()}</td>
                      <td className="py-3 pr-4 text-center font-mono text-ink-secondary">{(row.avgScore ?? 0).toLocaleString()}</td>
                    </>
                  ) : (
                    <>
                      <td className="py-3 pr-4 text-center font-mono text-green-400">{row.wins}</td>
                      <td className="py-3 pr-4 text-center font-mono text-ink-secondary">{row.draws}</td>
                      <td className="py-3 pr-4 text-center font-mono text-red-400">{row.losses}</td>
                      <td className="py-3 pr-4 text-center font-mono text-ink-secondary">{row.gamesWon}</td>
                      <td className="py-3 pr-4 text-center font-mono text-ink-secondary">{row.gamesLost}</td>
                    </>
                  )}
                  <td className="py-3 pr-3 text-center font-mono font-bold text-accent-500">{row.points.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RecentMatches({ compId }: { compId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['matches', compId],
    queryFn: () => api.matches(compId),
    enabled: !!compId,
  });

  if (isLoading) return null;
  const matches = (data || []).slice(0, 5);
  if (matches.length === 0) return null;

  return (
    <Card className="mt-6">
      <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-ink-secondary">
        <Swords className="h-4 w-4 text-ink-meta" /> Recent Results
      </h3>
      <div className="space-y-2">
        {matches.map(m => (
          <Link
            key={m.sk}
            to={`/match/${encodeURIComponent(m.sk)}?compId=${encodeURIComponent(m.pk)}`}
            className={cn('flex items-center justify-between rounded-lg bg-white/[0.04] px-4 py-3 transition-colors hover:bg-white/[0.07]', MORPH)}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <span className="font-medium text-ink-primary truncate">{m.clan1}</span>
              <span className="text-xs text-ink-meta shrink-0">vs</span>
              <span className="font-medium text-ink-primary truncate">{m.clan2}</span>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {m.createdAt && (
                <span className="hidden text-[10px] font-mono text-ink-meta sm:block">{formatMatchDate(m.createdAt)}</span>
              )}
              {m.nightWinner && (
                <span className="text-xs text-accent-500">🏆 {m.nightWinner}</span>
              )}
              <StatusBadge status={m.status} />
              <ChevronRight className="h-4 w-4 text-ink-meta" />
            </div>
          </Link>
        ))}
      </div>
    </Card>
  );
}

// ── Clan roster card (lazy-loads on first expand, cached after) ───────────────

function ClanRosterCard({ name }: { name: string }) {
  const [open, setOpen] = useState(false);

  const { data, isFetching } = useQuery({
    queryKey: ['clan', name],
    queryFn: () => api.clan(name),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const seats = data?.seats ?? [];
  const playerCount = data ? seats.length : null;

  return (
    <div className="relative rounded-lg bg-white/[0.04] overflow-visible">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center justify-between px-3 py-2.5 text-left hover:bg-white/[0.07] transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="truncate text-sm font-medium text-ink-primary">{name}</span>
          {playerCount !== null && (
            <span className="shrink-0 rounded-full bg-white/[0.06] px-1.5 py-0.5 font-mono text-xs text-ink-secondary">
              {playerCount}
            </span>
          )}
        </div>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-ink-meta transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-10 mt-1 rounded-lg bg-paper-panel px-3 py-2 shadow-[0_12px_32px_-6px_rgba(0,0,0,0.6)]">
          {isFetching && !data ? (
            <p className="py-1 text-xs text-ink-meta">Loading…</p>
          ) : seats.length === 0 ? (
            <p className="py-1 text-xs text-ink-meta">No roster data.</p>
          ) : (
            <ul className="space-y-1">
              {seats.map(s => (
                <li key={s.gamertag} className="flex items-center justify-between gap-2">
                  <span className="truncate font-mono text-xs text-ink-secondary">{s.gamertag}</span>
                  {s.status === 'CLAIMED' ? (
                    <span className="shrink-0 rounded-full bg-green-500/15 px-1.5 py-0.5 text-xs text-green-400">linked</span>
                  ) : s.status === 'PENDING_CLAIM' ? (
                    <span className="shrink-0 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-xs text-amber-400">pending</span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function ClanRosterGrid({ compId }: { compId: string }) {
  const { data: clans, isLoading } = useQuery({
    queryKey: ['competitionClans', compId],
    queryFn: () => api.competitionClans(compId),
    enabled: !!compId,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) return <PageSpinner />;
  const list = clans ?? [];
  if (list.length === 0) return null;

  return (
    <div className="mt-6">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-ink-secondary">
        <Users className="h-4 w-4 text-ink-meta" /> Teams & Rosters
        <span className="rounded bg-white/[0.05] px-1.5 py-0.5 text-xs font-normal text-ink-secondary">{list.length}</span>
      </h3>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {list.map(c => (
          <ClanRosterCard key={c.name} name={c.name} />
        ))}
      </div>
    </div>
  );
}

export function Standings() {
  const { activeComp, activeCompId, isLoading } = useWorkspace();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });
  // View-as-aware role — the admin gates below read THIS, not the raw /me
  // role, so a founder previewing a lower role loses the public-page toggle
  // (a real write against a real comp) and the stats export rather than
  // keeping them live mid-demo. Declared before the early return to satisfy
  // the Rules of Hooks. Identity no-op with no view-as — see lib/viewAs.ts.
  const effectiveRole = useEffectiveRole(me?.role);

  if (isLoading) return <PageSpinner />;

  if (!activeCompId) {
    return (
      <EmptyState
        title="Nothing forged yet"
        message="Pick a competition from the sidebar to see who's on top."
        icon={<Trophy className="h-12 w-12" strokeWidth={1.5} />}
      />
    );
  }

  return (
    <div>
      <div className={cn('mb-8 flex flex-col gap-3 md:flex-row md:items-center md:justify-between', SETTLE)} style={settleDelay(0)}>
        <div className="min-w-0">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-ink-meta">
            Standings
          </p>
          <h1 className="mt-1 truncate font-display text-3xl font-black uppercase tracking-tight text-white sm:text-4xl">
            {activeComp?.name || activeCompId.replace('COMP#', '')}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdminRole(effectiveRole) && (
            <PublicPageToggle compId={activeCompId} enabled={activeComp?.publicPageEnabled === true} />
          )}
          {isAdminRole(effectiveRole) && (
            <ExportStatsButton
              compId={activeCompId}
              compName={activeComp?.name || activeCompId.replace('COMP#', '')}
            />
          )}
          {activeComp?.status && <StatusBadge status={activeComp.status} />}
        </div>
      </div>

      <div className={SETTLE} style={settleDelay(1)}>
        <Card>
          <StandingsTable compId={activeCompId} />
        </Card>
      </div>

      <div className={SETTLE} style={settleDelay(2)}>
        <SeasonProgression compId={activeCompId} />
      </div>
      <div className={SETTLE} style={settleDelay(3)}>
        <RecentMatches compId={activeCompId} />
      </div>
      <div className={SETTLE} style={settleDelay(4)}>
        <ClanRosterGrid compId={activeCompId} />
      </div>
    </div>
  );
}
